import { useEffect } from 'react'
import { WorkspaceFontMeta } from 'types'
import { getGoogleFont, loadFont, useOnline } from 'utils'
import { useExplorerConfig } from '../components/providers'
import {
  ensureDirectoryExists,
  fileExists,
  makeFileSrc,
  readFile,
  saveFile,
  writeFile,
} from './filesystem'
import { isDesktop } from './helpers'

export function useLoadFont() {
  const online = useOnline()
  const { theme } = useExplorerConfig()

  useEffect(() => {
    if (!theme) return

    const family = theme.fontFamily || 'Inter'
    if (family === 'Inter') return

    if (isDesktop) {
      getOrDownloadFont(family, online)
    } else {
      getGoogleFont(family).then(loadFont)
    }
  }, [online, theme])
}

export async function getOrDownloadFont(family: string, online: boolean) {
  let font: WorkspaceFontMeta | null = null

  if (online) {
    const remoteFont = await getGoogleFont(family)
    const isStored = await isStoredFontCurrent(remoteFont)
    if (!isStored) await storeFontToDevice(remoteFont)
  }

  font = await getStoredFont(family)
  if (font) await loadFont(font)
}

async function isStoredFontCurrent(font: WorkspaceFontMeta): Promise<boolean> {
  const storedFont = await getStoredFont(font.family)
  return storedFont?.version === font.version
}

async function storeFontToDevice(font: WorkspaceFontMeta) {
  const { family, files } = font
  await ensureDirectoryExists(getFontDirectory(family))

  const localFiles: { [variant: string]: string } = {}

  for (const [variant, url] of Object.entries(files)) {
    const filePath = getFontFilePath(family, variant, url)
    await saveFile(url, filePath)
    localFiles[variant] = makeFileSrc(filePath)
  }

  const fontWithLocalPaths = { ...font, files: localFiles }
  const fontJsonPath = getFontJsonPath(family)
  await writeFile(fontJsonPath, JSON.stringify(fontWithLocalPaths, null, 2))
}

async function getStoredFont(fontFamily: string): Promise<WorkspaceFontMeta | null> {
  try {
    const fontJsonPath = getFontJsonPath(fontFamily)
    const exists = await fileExists(fontJsonPath)

    if (!exists) return null

    const storedFontJson = await readFile(fontJsonPath)
    return JSON.parse(storedFontJson)
  } catch {
    return null
  }
}

const getFontDirectory = (fontFamily: string) => `fonts/${fontFamily}`

function getFontJsonPath(fontFamily: string): string {
  const fontDir = getFontDirectory(fontFamily)
  return `${fontDir}/font.json`
}

function getFontFilePath(fontFamily: string, variant: string, url?: string): string {
  const fontDir = getFontDirectory(fontFamily)
  const extension = url?.split('.').pop()?.split(/\?|#/)[0] || 'ttf'
  const fileName = `${variant}.${extension}`
  return `${fontDir}/${fileName}`
}
